// components/cart/AddressSelector.js
import React, { useState, useEffect } from 'react';
import tw from 'twin.macro';
import { obtenerDireccionesUsuario } from 'scripts/obtenerDireccionesUsuario';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { crearDireccion } from 'scripts/crearDireccion';
import { setCostoEnvio, setDireccion, setZonaEnvio, setZonaEnvioNombre } from 'store/carritoSlice';
import { obtenerCostoEnvio } from 'scripts/obtenerCostoEnvio';
import DireccionesModal from '../DireccionesModal';
import { Star } from '@mui/icons-material';
import { AiOutlineInfoCircle } from 'react-icons/ai';

const Container = tw.div`flex flex-col w-full`;
const Title = tw.h2`text-2xl font-bold mb-4`;
const AddressList = tw.div`bg-gray-100 p-4 rounded-lg shadow-md`;
const AddressItem = tw.div`flex justify-between items-center border-b pb-2 mb-2 cursor-pointer p-2 rounded relative`;
const StarIcon = tw(Star)`absolute top-0 right-0 m-2 text-yellow-500 z-10`;
const Buttons = tw.div`flex justify-between`;
const SelectButton = tw.button`mt-4 px-6 py-3 bg-blue-600 text-white rounded hover:bg-blue-800 self-start`;
const NextButton = tw.button`mt-4 px-6 py-3 bg-blue-600 text-white rounded hover:bg-blue-800 self-end`;
const ViandasSchedule = tw.div`flex items-start gap-2 p-3 mb-4 rounded-lg bg-blue-100 border border-blue-300`;
const ViandasScheduleText = tw.p`text-base font-medium text-blue-700`;

const AddressSelector = ({ step, setStep, direccionId, soloViandas = false }) => {
	const dispatch = useDispatch();
	const [direcciones, setDirecciones] = useState([]);
	const [mostrarModalDirecciones, setMostrarModalDirecciones] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [direccionValida, setDireccionValida] = useState(false);
	const token = sessionStorage.getItem('token');
	const user = useSelector((state) => state.usuario);
	let userId = null;
	if (user.isLoggedIn) {
		userId = user.userInfo.id;
	}
	const pedidoId = useSelector((state) => state.carrito.pedidoId);

	const seleccionarDireccion = async (id, { mostrarToastExito = true } = {}) => {
		try {
			const data = await obtenerCostoEnvio(id, pedidoId, token);
			if (data && data.costoEnvio !== undefined && data.costoEnvio !== null) {
				dispatch(setDireccion(id));
				if (mostrarToastExito) {
					toast.success(
						soloViandas ? 'Dirección seleccionada. El envío diario está incluido.' : 'Costo de envío actualizado.',
					);
				}
				dispatch(setCostoEnvio(soloViandas ? 0 : data.costoEnvio));
				dispatch(setZonaEnvio(data.zona_id));
				dispatch(setZonaEnvioNombre(data.zona_nombre || ''));
				setDireccionValida(true);
				return true;
			}

			toast.error('Error al obtener el costo de envío.');
			dispatch(setDireccion(0));
			dispatch(setCostoEnvio(0));
			dispatch(setZonaEnvio(0));
			dispatch(setZonaEnvioNombre(''));
			setDireccionValida(false);
			return false;
		} catch (e) {
			toast.error(e.message || 'Error al obtener el costo de envío.');
			dispatch(setDireccion(0));
			dispatch(setCostoEnvio(0));
			dispatch(setZonaEnvio(0));
			dispatch(setZonaEnvioNombre(''));
			setDireccionValida(false);
			return false;
		}
	};

	const fetchDirecciones = async () => {
		try {
			const data = await obtenerDireccionesUsuario(userId, token);
			setDirecciones(data);
			if (direccionId > 0) {
				await seleccionarDireccion(direccionId, { mostrarToastExito: false });
				return;
			}

			const direccionDefault = data.find((direccion) => direccion.predeterminado);
			if (direccionDefault && !direccionId) {
				if (step === 3) {
					await seleccionarDireccion(direccionDefault.id, { mostrarToastExito: false });
				}
			}
		} catch (error) {
			toast.error(error.message || 'Error al obtener las direcciones.');
		} finally {
			setIsLoading(false);
		}
	};

	const guardarDireccion = async (direccion) => {
		try {
			const data = await crearDireccion(direccion, token);
			await fetchDirecciones();
			await seleccionarDireccion(data.id);
			toast.success('Dirección guardada correctamente.');
			return true;
		} catch (e) {
			toast.error(e.message || 'Error al guardar la dirección.');
			return false;
		}
	};

	useEffect(() => {
		if (userId) {
			fetchDirecciones();
		}
	}, [step]);

	const puedeContinuar = direccionId > 0 && direccionValida;

	return (
		<Container>
			<Title>Selecciona una dirección</Title>
			{soloViandas ? (
				<ViandasSchedule>
					<AiOutlineInfoCircle tw="text-blue-700 mt-1 flex-shrink-0 text-lg" />
					<ViandasScheduleText>Horario de entrega de viandas: 11:30 a 14:00</ViandasScheduleText>
				</ViandasSchedule>
			) : (
				<ViandasSchedule>
					<AiOutlineInfoCircle tw="text-blue-700 mt-1 flex-shrink-0 text-lg" />
					<ViandasScheduleText>Horario de envío: entre 18 y 21 hs</ViandasScheduleText>
				</ViandasSchedule>
			)}
			{isLoading ?
				<p>Cargando direcciones...</p>
			:	<AddressList>
					{direcciones.length > 0 ?
						direcciones.map((direccion) => (
							<AddressItem
								key={direccion.id}
								onClick={() => seleccionarDireccion(direccion.id)}
								style={{ backgroundColor: direccionId === direccion.id ? '#80b59f' : '#f7f7f7' }}
							>
								{direccion.predeterminado && (
									<StarIcon style={{ color: direccionId === direccion.id ? 'white' : '#f1df00' }} />
								)}
								<div style={{ color: direccionId === direccion.id ? 'white' : 'black' }}>
									<p>{direccion.titulo}</p>
									<p>{direccion.barrio}</p>
									<p>{direccion.direccion}</p>
								</div>
							</AddressItem>
						))
					:	<p>No tienes direcciones registradas.</p>}
					<div tw="mt-4">
						<button
							onClick={() => setMostrarModalDirecciones(true)}
							tw="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-800"
						>
							+ Agregar Dirección
						</button>
					</div>

					{/* Modal para agregar direcciones */}
					<DireccionesModal
						userId={userId}
						mostrarModalDirecciones={mostrarModalDirecciones}
						setMostrarModalDirecciones={setMostrarModalDirecciones}
						onGuardarDireccion={guardarDireccion}
					/>
				</AddressList>
			}
			<Buttons>
				<SelectButton onClick={() => setStep(2)}>Volver</SelectButton>
				<NextButton
					onClick={() => setStep(4)}
					disabled={!puedeContinuar}
					style={{
						backgroundColor: puedeContinuar ? '#2563eb' : '#9ca3af',
						cursor: puedeContinuar ? 'pointer' : 'not-allowed',
					}}
				>
					Continuar
				</NextButton>
			</Buttons>
		</Container>
	);
};

export default AddressSelector;
